import Logo from "../../assets/logo/1800AccountantLogo.svg";
import React, { useEffect, useState } from "react";
import { Form, Formik } from "formik";
import {
    Box,
    Button,
    Container,
    Typography,
    useMediaQuery,
} from "@mui/material";
import UiFormControlSelection from "../common/ui/UiFormControlSelection";
import {
    ERRORS,
    ONBOARDING_STEPS_ROUTES,
    ROUTES,
} from "../constants/user-info.const";
import {
    loginService,
    setUserLoggedIn,
    storeAuthToken,
    getConfig,
    storeBusinessName,
} from "../../services/authService";
import { useNavigate } from "react-router-dom";
import store from "../../store";
import { showAlert } from "../../store/actions/feedback";
import Loader from "../common/Loader";
import { setConfig } from "../../store/actions/config";
import * as Yup from "yup";
import { getOnboardingSteps } from "../../services/employeeService";
import { onboardingActions } from "../../store/actions/steps";
import { formatDateYYMMDD, unixTimeStampToDate } from "../../utils/dateUtil";
import { setUserType } from "../../utils/login";
import { useAppState } from "../../provider/AppProvider";

interface FormFields {
    dob: string;
}
const initialValues = {
    dob: "",
};

export default function SignIn() {
    const [accessToken, setAccessToken] = useState("");
    const [businessName, setBusinessName] = useState("");
    const [showLoader, setShowLoader] = useState(false);
    const { loadEmployeeData, logo, isApiLoading } = useAppState();
    const navigate = useNavigate();
    const [businessInitial, setBusinessInitial] = useState("");
    const smDevice = useMediaQuery("(max-width:600px)");

    const allowUserLogin = () => {
        setUserLoggedIn("true");
    };

    const updateOnboardingStepsInStore = (onboardingStepsStatus) => {
        store.dispatch({
            type: onboardingActions.SET_ONBOARDING_STEPS,
            state: onboardingStepsStatus,
        });
    };

    const navigateToCurrentStep = (currentStep: string): void => {
        currentStep && ONBOARDING_STEPS_ROUTES[currentStep]
            ? navigate(ONBOARDING_STEPS_ROUTES[currentStep])
            : navigate(ROUTES.personalInfo);
    };

    const showErrorMessage = (error) => {
        const errorMessage = error.status?.message;
        store.dispatch(
            showAlert({
                alertType: "error",
                alertText: errorMessage,
            })
        );
    };

    const validationSchema = Yup.object({
        dob: Yup.date().required(ERRORS.dob),
    });

    const setExpiryDate = (time: number) => {
        const expiryDate = unixTimeStampToDate(time);
        localStorage.setItem('adpExpiryDate', unixTimeStampToDate(time, "MMM DD, YYYY • h:mm A"))
        localStorage.setItem("expiryDate", expiryDate);
    };

    const returnDatePicker = () => {
        if (!smDevice) {
            return (
                <UiFormControlSelection
                    label="Date of Birth (Required)"
                    type="date"
                    fieldName="dob"
                    dateType="string"
                    errorMessage={ERRORS.dob}
                    maxDate={new Date()}
                    showFloatingLabel={true}
                    placeholder="MM/DD/YYYY"
                    labelClass="date-picker"
                ></UiFormControlSelection>
            );
        }
        return (
            <UiFormControlSelection
                label="Date of Birth (Required)"
                type="dateMobile"
                fieldName="dob"
                dateType="string"
                errorMessage={ERRORS.dob}
                maxDate={new Date()}
                showFloatingLabel={true}
                placeholder="MM/DD/YYYY"
                dateFormat="MM/dd/yyyy"
                labelClass="date-picker"
            ></UiFormControlSelection>
        );
    };

    const removeQueryParams = () => {
        if (window.location.href.includes("?")) {
            window.history.pushState(
                {},
                "",
                window.location.href.split("?")[0]
            );
        }
    };

    const getTokenFromUrl = (): string => {
        const url = window.location.href;
        const token = url.match(/token=(.*?)&name/i);
        if (token) {
            return token[1];
        }
        return "";
    };

    const getBusinessTokenFromUrl = (url): string => {
        return new URLSearchParams(url.search).get("name") ?? "";
    };

    useEffect(() => {
        const url = window.location;
        const accessToken = getTokenFromUrl();
        const businessName = getBusinessTokenFromUrl(url);
        if (accessToken) {
            setAccessToken(accessToken);
        }
        if (businessName) {
            setBusinessName(businessName);
            setBusinessInitial(businessName.charAt(0));
        }
    }, [accessToken, businessName]);

    const setOnBoardingSteps = (data) => {
        const currentStep = data.current_step;
        const onBoardingSteps = data.onboarding_steps_status;
        navigateToCurrentStep(currentStep);
        updateOnboardingStepsInStore(onBoardingSteps);
    };

    const getConfigAndOnboardingSteps = () => {

        Promise.all([getConfig(), getOnboardingSteps()])
            .then((results) => {
                setShowLoader(false);
                const [config, onboardingSteps] = results;
                allowUserLogin();
                removeQueryParams();
                store.dispatch(setConfig((config as any).data));
                setOnBoardingSteps((onboardingSteps as any).data);
            })
            .catch((error) => {
                setShowLoader(false);
                showErrorMessage(error);
            });
    };

    const adpLoginSuccess = () => {
        setShowLoader(false);
        allowUserLogin();
        removeQueryParams();
        loadEmployeeData();
        navigate("adp-personal-info");
    };

    const invokeLoginAPI = () => {
        loginService()
            .then((result: any) => {
                const data = result.data as any;
                setExpiryDate(data.expiryTime);
                setUserType(data?.is_adp ?? false);
                if (data?.is_adp) {
                    adpLoginSuccess();
                } else {
                    getConfigAndOnboardingSteps();
                }
            })
            .catch((error) => {
                setShowLoader(false);
                showErrorMessage(error);
            });
    };

    const onSubmit = (data: FormFields) => {
        const date = data.dob;
        if (!date) return;
        const dateValue = formatDateYYMMDD(date);
        const loginToken = dateValue + "_" + accessToken;
        setShowLoader(true);
        storeAuthToken(loginToken);
        storeBusinessName(businessName);
        invokeLoginAPI();
    };

    if(isApiLoading){
        return <Loader />
    }

    return (
        <>
            <Container maxWidth={false} className="sign-in">
                <div className="sign-in--container">
                    {showLoader ? (
                        <Loader />
                    ) : (
                        <Box>
                            <div className="sign-in--logo">
                                <img
                                    src={logo}
                                    alt="1800-Accountant logo"
                                ></img>
                            </div>

                            <Typography variant="h4" className="sign-in--title">
                                Employee Payroll Sign In
                            </Typography>
                            <div className="sign-in--initial">
                                <div className="circle-filled">
                                    <span>{businessInitial}</span>
                                </div>
                                <Typography
                                    variant="h6"
                                    className="sign-in--businessName"
                                >
                                    {businessName}
                                </Typography>
                            </div>
                            <Formik
                                initialValues={initialValues}
                                onSubmit={onSubmit}
                                enableReinitialize={true}
                                validationSchema={validationSchema}
                            >
                                {() => {
                                    return (
                                        <Form autoComplete="off">
                                            <div className="sign-in--datePicker">
                                                {returnDatePicker()}

                                                <span className="date-format">
                                                    MM/DD/YYYY
                                                </span>
                                            </div>
                                            <div className="sign-in--submit">
                                                <Button
                                                    className="sign-in--container__submit btn-primary"
                                                    type="submit"
                                                >
                                                    Sign In
                                                </Button>
                                            </div>
                                        </Form>
                                    );
                                }}
                            </Formik>
                        </Box>
                    )}
                </div>
            </Container>
        </>
    );
}
